import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import isObject from 'lodash/isObject';

import { type Board, type BoardsListOptions, type Library, SortDirection, type SubnavBoardSort } from '@air/api/types';

import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';

export const BOARD_SUBBOARDS = 'BOARD_SUBBOARDS';

export type BoardSubBoardsKeyParams = {
  boardId: Board['id'];
  sortField?: SubnavBoardSort;
  libraryId?: Library['id'];
};

export type BoardSubBoardsKey = [typeof BOARD_SUBBOARDS, BoardSubBoardsKeyParams];

export const getBoardSubBoardsKey = (
  boardId: Board['id'],
  sortField?: SubnavBoardSort,
  libraryId?: Library['id'],
): BoardSubBoardsKey => {
  return [BOARD_SUBBOARDS, { boardId, sortField, libraryId }];
};

export const isBoardSubBoardsKey = (queryKey: QueryKey, libraryId?: Library['id']): queryKey is BoardSubBoardsKey => {
  if (!Array.isArray(queryKey) || queryKey[0] !== BOARD_SUBBOARDS || !isObject(queryKey[1])) {
    return false;
  }

  if (libraryId !== undefined) {
    const keyParams = queryKey[1];

    return 'libraryId' in keyParams && keyParams.libraryId === libraryId;
  }

  return true;
};

export type UseSubBoardsListParams = {
  boardId: string;
  canFetchData: boolean;
  libraryId?: Library['id'];
  params?: Partial<BoardsListOptions>;
};

export const useSubBoardsList = ({ boardId, canFetchData, libraryId, params }: UseSubBoardsListParams) => {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  // Sub-boards within a library use the library's sort, otherwise use workspace sort
  const subnavSort = getSubNavSortValue(libraryId);
  const sortField = libraryId ? subnavSort?.libraryBoardSorts?.[0] : subnavSort?.boardSort;

  const inifiteQuery = useInfiniteQuery({
    queryKey: getBoardSubBoardsKey(boardId, sortField, libraryId),
    queryFn: ({ pageParam }) =>
      privateBoardsFetcher({
        params: {
          isMemberOfBoard: 'ALL',
          view: boardId,
          numThumbnails: 0,
          includeAncestors: true,
          includeDirectDescendantBoardCount: true,
          sortField: sortField ? { name: sortField.name, direction: sortField.direction } : undefined,
          ...params,
        },
        sortFieldName: sortField?.name || 'customSort',
        sortDirection: sortField?.direction || SortDirection.desc,
        cursor: pageParam || null,
      }),
    enabled: !!boardId && canFetchData,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  return {
    data: inifiteQuery.data?.pages.flatMap((d) => d.data) ?? [],
    total: inifiteQuery.data?.pages[inifiteQuery.data.pages.length - 1]?.total ?? 0,
    hasMore: !!inifiteQuery.hasNextPage,
    loadNextPage: inifiteQuery.fetchNextPage,
    isLoadingMore: inifiteQuery.isFetchingNextPage,
    isInitialLoading: inifiteQuery.isLoading,
  };
};
